import React, { ReactElement } from 'react';
import {
  CssfTableData,
  generateCssfColumns,
  generateCssfData,
} from './CssfRiskSpreading.data';
import CustomTable from '../../../tables/CustomTable';
import GridItem from '../../../layout/GridComponents/GridItem';
import CssfDetailComponent from './detailPanel/CssfDetail.component';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useCssfRiskSpreadingPageModel } from '../../../../api/rs-hg/risk/hooks/queries';
import type {
  CssfRiskSpreadingFund,
  CssfRiskSpreadingPageModel,
} from '../../../../api/rs-hg/risk/cssfRiskSpreading.types';

interface TableProps {
  data: CssfTableData[];
  fund: CssfRiskSpreadingFund;
  fundId: string;
  positionDate?: string;
}

const CssfTable = ({
  data,
  fund,
  fundId,
  positionDate,
}: TableProps): ReactElement => {
  const columns = generateCssfColumns(fundId);

  return (
    <GridItem xs={12} card>
      <CustomTable<CssfTableData>
        options={{
          paging: false,
          search: true,
          exportButton: true,
          raptorExport: true,
        }}
        id={'cssf-risk-spreading-table'}
        columns={columns}
        csvFields={['number', 'ruleTitle', 'value', 'limit', 'status']}
        data={data}
        showToolbar={true}
        title={fund.fund_name_full || fund.fund_name}
        detailPanel={(rowData: CssfTableData) => (
          <CssfDetailComponent
            ruleTitle={rowData.ruleTitle}
            fund={fund}
            positionDate={positionDate}
            ruleNumber={rowData.number}
            isCounterparty={rowData.ruleTitle
              .toLowerCase()
              .includes('counterparty')}
          />
        )}
      />
    </GridItem>
  );
};

function CssfRiskSpreading(props: FundInfoComponentProps): ReactElement {
  const { fundId, positionDate } = props;

  const {
    data: pageModel,
    isLoading,
    isFetching,
    isError,
    error,
  } = useCssfRiskSpreadingPageModel(fundId, positionDate);

  return (
    <QueryBoundary<CssfRiskSpreadingPageModel>
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      error={error}
      data={pageModel}
      loadingMessages={['Loading CSSF Risk Spreading Data...']}
      errorMessage="No Data Available."
      noDataMessage="No Data Available."
    >
      {(page) => (
        <CssfTable
          data={generateCssfData(page.data)}
          fund={page.fund}
          fundId={fundId}
          positionDate={page.returned_position_date || positionDate}
        />
      )}
    </QueryBoundary>
  );
}

export default CssfRiskSpreading;
